import { useRef, useState } from 'react'
import { CATEGORY_SETUP, useEditor, type LightCategory } from '../store/useEditor'
import { isStrand } from '../types/document'
import { EXTRA_LINES, lineTotal, PRICE_PRESETS, WORDS, type QuoteItem } from '../lib/quote'
import {
  balanceDue,
  defaultPaper,
  depositAmount,
  discount,
  discountLabel,
  docNumber,
  longDate,
  money,
  MONTHS,
  paidAmount,
  totalAt,
  subtotal,
  total,
  type DepositMode,
  type DueOn,
} from '../lib/paper'
import type { QuoteLines } from '../lib/quote'
import QuoteDoc from './QuoteDoc'

/**
 * WHERE THE ESTIMATE IS TYPED.
 *
 * ONE SHAPE FOR EVERY LINE:  how many  x  each  x  price per
 *
 *   roofline   240 ft     x 1         x $2.50 /ft
 *   garland      3 pieces x 1         x $150  each
 *   wreaths      2        x 1         x $200  each
 *   palms        4 trees  x 6 strands x $35   /strand
 *
 * The middle number is the one only palms and trees need, and it is why a
 * single shape covers the trade instead of four special cases. It hides itself
 * where it is always one.
 *
 * ONE LINE PER KIND OF WORK, not one per run. A house has six roof edges and
 * the customer is buying "the roofline" - nobody itemizes a fascia. The
 * drawing has as many runs as the roof has; the quote has one line, and the
 * footage is the number measured with a wheel in the driveway.
 *
 * EVERYTHING ELSE IS FOLDED AWAY. The company details are typed once in a
 * lifetime, the terms once a season, the seasonal ladder once a season. Those
 * three sections are closed by default so that opening this screen on the
 * hundredth house shows a list of prices and nothing else.
 *
 * A QUOTE BECOMES AN INVOICE HERE. Same document, same number, same lines: the
 * word changes and a deposit gets recorded. Nothing is copied.
 */

const ORDER: LightCategory[] = [
  'roofline',
  'garland',
  'palms',
  'trees',
  'clusters',
  'columns',
  'wreaths',
  'accessories',
  'other',
]

/** What kind of Strand each category draws, for the count beside the line. */
const KIND_OF: Partial<Record<LightCategory, string>> = {
  roofline: 'roofline',
  garland: 'swag',
  palms: 'wrap',
  trees: 'wrap',
  columns: 'wrap',
  clusters: 'cluster',
  other: 'freeform',
}

/** The month after the last rung, so adding one lands somewhere sensible. */
const nextMonth = (tiers: { month: number }[]) =>
  tiers.length === 0 ? new Date().getMonth() : (tiers[tiers.length - 1].month + 1) % 12

const num = (v: string) => {
  const f = parseFloat(v)
  return Number.isFinite(f) ? f : 0
}

/* ------------------------------------------------------------------ line --- */

function Line({ item }: { item: QuoteItem }) {
  const setQuoteLine = useEditor((s) => s.setQuoteLine)
  const removeQuoteLine = useEditor((s) => s.removeQuoteLine)
  const doc = useEditor((s) => s.doc)

  /*
   * How many of this kind are on the house, said out loud and filled in
   * nowhere. The drawing is a reminder of what was agreed, not a measurement:
   * see lib/quote.ts for why no quantity is ever read off the picture.
   */
  const kind = KIND_OF[item.category]
  const drawn = kind
    ? doc.layers.filter((l) => isStrand(l) && l.kind === kind).length
    : doc.layers.filter((l) => l.type === 'decoration').length

  const w = WORDS[item.category]
  const set = (patch: Partial<QuoteItem>) => setQuoteLine(item.id, patch)

  /*
   * The prices offered are this category's starting points plus anything
   * already typed in this quote. Never a settings screen: a price typed once
   * is a suggestion from then on.
   */
  const presets = [
    ...new Set([...PRICE_PRESETS[item.category], ...(item.price > 0 ? [item.price] : [])]),
  ].sort((a, b) => a - b)

  return (
    <div className="quote-line">
      <div className="ql-top">
        <input
          className="ql-desc"
          type="text"
          value={item.label ?? ''}
          placeholder={CATEGORY_SETUP[item.category].label}
          onChange={(e) => set({ label: e.target.value })}
        />
        {drawn > 0 && <span className="ql-count">{drawn} on the house</span>}
        <button className="ql-x" onClick={() => removeQuoteLine(item.id)} aria-label="Remove line">
          &times;
        </button>
      </div>

      <div className="ql-fields">
        <label>
          <span>{w.count}</span>
          <input
            type="number"
            inputMode="decimal"
            min={0}
            value={item.count || ''}
            placeholder="0"
            onChange={(e) => set({ count: num(e.target.value) })}
          />
        </label>

        {w.each && (
          <label>
            <span>{w.each}</span>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              step="1"
              value={item.each || ''}
              placeholder="1"
              onChange={(e) => set({ each: Math.round(num(e.target.value)) })}
            />
          </label>
        )}

        <label>
          <span>{w.price}</span>
          {/*
            Suggestions on a normal number field, so it opens as a dropdown and
            still takes anything typed. A rate card that has to be configured
            before a price can be written is a rate card nobody fills in.
          */}
          <input
            type="number"
            inputMode="decimal"
            min={0}
            step="0.25"
            list={`p-${item.id}`}
            value={item.price || ''}
            placeholder="0.00"
            onChange={(e) => set({ price: num(e.target.value) })}
          />
          <datalist id={`p-${item.id}`}>
            {presets.map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
        </label>

        <span className="ql-total">{money(lineTotal(item))}</span>
      </div>

      <div className="ql-row2">
        <input
          type="text"
          value={item.lighting}
          placeholder="Warm White"
          onChange={(e) => set({ lighting: e.target.value })}
        />
        <input
          type="text"
          value={item.note}
          placeholder="Note for the customer, if any"
          onChange={(e) => set({ note: e.target.value })}
        />
      </div>
    </div>
  )
}

/* --------------------------------------------------------------- company --- */

function CompanyFields() {
  const company = useEditor((s) => s.company)
  const setCompany = useEditor((s) => s.setCompany)
  const file = useRef<HTMLInputElement>(null)

  const pickLogo = (f: File | undefined) => {
    if (!f) return
    const r = new FileReader()
    // A data URL, so it survives a reload with no server and no upload. The
    // logo never leaves this browser.
    r.onload = () => setCompany({ logoDataUrl: String(r.result) })
    r.readAsDataURL(f)
  }

  return (
    <div className="qf">
      <div className="qf-logo">
        {company.logoDataUrl ? (
          <img src={company.logoDataUrl} alt="" />
        ) : (
          <span>No logo yet</span>
        )}
        <div>
          <button className="btn" onClick={() => file.current?.click()}>
            {company.logoDataUrl ? 'Replace logo' : 'Upload logo'}
          </button>
          {company.logoDataUrl && (
            <button className="btn" onClick={() => setCompany({ logoDataUrl: null })}>
              Remove
            </button>
          )}
          <p className="hint">Shows white on black, so use a light or transparent logo.</p>
        </div>
        <input
          ref={file}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => pickLogo(e.target.files?.[0])}
        />
      </div>

      <label>
        <span>Company name</span>
        <input
          type="text"
          value={company.name}
          placeholder="Your company name"
          onChange={(e) => setCompany({ name: e.target.value })}
        />
      </label>
      <label>
        <span>Address</span>
        <input
          type="text"
          value={company.address}
          placeholder="Street, city, state, ZIP"
          onChange={(e) => setCompany({ address: e.target.value })}
        />
      </label>
      <label>
        <span>Phone</span>
        <input
          type="text"
          value={company.phone}
          placeholder="(555) 555-5555"
          onChange={(e) => setCompany({ phone: e.target.value })}
        />
      </label>
      <label>
        <span>Email</span>
        <input
          type="text"
          value={company.email}
          onChange={(e) => setCompany({ email: e.target.value })}
        />
      </label>
      <label>
        <span>Number prefix</span>
        <input
          type="text"
          value={company.prefix}
          placeholder="LP"
          onChange={(e) => setCompany({ prefix: e.target.value })}
        />
      </label>
      <label>
        <span>License, if it has to appear</span>
        <input
          type="text"
          value={company.license}
          onChange={(e) => setCompany({ license: e.target.value })}
        />
      </label>
    </div>
  )
}


/* ------------------------------------------------------------ payments --- */

/**
 * WRITING DOWN MONEY THAT ARRIVED. This is not a checkout.
 *
 * Payments in this trade happen outside the app: a check on the kitchen table,
 * a Zelle from the truck, a card run on somebody else's terminal. Nothing here
 * charges anybody and nothing here sends anybody a link. It is the page where
 * a contractor writes down what he has been handed, so the balance on the job
 * list is the truth rather than an assumption.
 *
 * A LIST, because part payments are ordinary: a deposit in September, most of
 * the balance in December, and the last hundred in January.
 */
function Payments({ lines }: { lines: QuoteLines }) {
  const paper = useEditor((s) => s.doc.paper ?? defaultPaper())
  const record = useEditor((s) => s.recordPayment)
  const remove = useEditor((s) => s.removePayment)

  const owed = balanceDue(lines, paper)
  const [amount, setAmount] = useState('')
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [method, setMethod] = useState('')

  const value = num(amount)
  const ok = value > 0 && !!date

  const add = () => {
    if (!ok) return
    record({ amount: value, date, method: method.trim() })
    setAmount('')
    setMethod('')
  }

  return (
    <div className="qpay">
      <p className="insp-lbl plain">Payments received</p>

      {paper.payments.length > 0 && (
        <ul className="qpay-list">
          {paper.payments.map((q) => (
            <li key={q.id}>
              <strong>{money(q.amount)}</strong>
              <em>
                {longDate(q.date)}
                {q.method ? ` · ${q.method}` : ''}
              </em>
              {/*
                A CORRECTION, NOT A DELETION OF THE TRUTH. Somebody typed 500
                where they meant 50, and leaving it there means a customer's
                balance is wrong. Undo puts it back.
              */}
              <button
                className="btn ghost"
                onClick={() => remove(q.id)}
                aria-label={`Remove the ${money(q.amount)} payment`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="qf">
        <label>
          <span>Amount</span>
          <input
            type="number"
            min={0}
            step="0.01"
            value={amount}
            placeholder={owed > 0 ? String(owed) : ''}
            onChange={(e) => setAmount(e.target.value)}
          />
        </label>
        <label>
          <span>Received</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label>
          <span>How they paid</span>
          <input
            type="text"
            value={method}
            placeholder="Check, Zelle, card, cash"
            onChange={(e) => setMethod(e.target.value)}
          />
        </label>
        <div className="qf-wide qpay-act">
          <button className="btn" onClick={add} disabled={!ok}>
            Record this payment
          </button>
          <span className="hint">
            {paidAmount(lines, paper) > 0
              ? `${money(paidAmount(lines, paper))} received, ${money(owed)} still owed.`
              : 'Nothing received yet. Recording a payment does not charge anybody.'}
          </span>
        </div>
      </div>
    </div>
  )
}

/* ----------------------------------------------------------------- screen -- */

export default function Quote({ onClose }: { onClose: () => void }) {
  const doc = useEditor((s) => s.doc)
  const company = useEditor((s) => s.company)
  const addQuoteLine = useEditor((s) => s.addQuoteLine)
  const setCustomer = useEditor((s) => s.setCustomer)
  const setPaper = useEditor((s) => s.setPaper)
  const setDeposit = useEditor((s) => s.setDeposit)
  const [showDoc, setShowDoc] = useState(false)
  /*
   * The months are edited in one place and chosen in another. Choosing is a
   * press per house; editing is once a season, so it stays folded - but the
   * press that opens it lives next to the prices, because that is the moment
   * anybody wants it.
   */
  const [ladder, setLadder] = useState(false)

  const lines = doc.quote ?? []
  const paper = doc.paper ?? defaultPaper()
  const invoice = paper.kind === 'invoice'
  const dep = depositAmount(lines, paper)
  const paid = paidAmount(lines, paper)

  /*
   * THE RUNGS, IN MONTH ORDER, PLUS THE ONE THIS JOB IS ON.
   *
   * A job installed in a month nobody put on the ladder is still a job with a
   * month on it, and the paper says so. Without the second half of this the
   * chips would show nothing pressed while the document read December install,
   * which is the kind of small disagreement that makes a person stop trusting
   * the screen.
   */
  const rungs = [...paper.tiers]
  if (paper.installMonth != null && !rungs.some((t) => t.month === paper.installMonth))
    rungs.push({ month: paper.installMonth, offPct: 0 })
  rungs.sort((a, b) => a.month - b.month)

  const setTier = (i: number, patch: { month?: number; offPct?: number }) =>
    setPaper({ tiers: paper.tiers.map((t, k) => (k === i ? { ...t, ...patch } : t)) })

  return (
    /*
      THE WS CLASS IS THE WHOLE CONVERSION.
      This sheet was the last dark screen in the product with no photograph on
      it, and the build order's rule for the editor chrome is one line: dark
      stays where a photograph is on screen. There is none here. Carrying ws
      brings the workspace's buttons, inputs, labels and palette with it, so
      the light treatment is the same one every other form in the product got
      rather than a second one invented for this screen.
    */
    <div className="quote-screen pw ws" role="dialog" aria-label="Quote">
      <header className="quote-head">
        <h2>{invoice ? 'Invoice' : 'Quote'}</h2>
        <button className="btn" onClick={() => setShowDoc(true)} disabled={lines.length === 0}>
          Preview
        </button>
        <button className="btn" onClick={onClose}>
          Done
        </button>
      </header>

      <div className="quote-lines">
        {/* Who it is for. The two fields anybody actually fills in. */}
        <div className="qf qf-top">
          <label>
            <span>Customer</span>
            <input
              type="text"
              value={doc.customer}
              placeholder="Customer name"
              onChange={(e) => setCustomer({ customer: e.target.value })}
            />
          </label>
          <label>
            <span>Phone</span>
            <input
              type="text"
              value={paper.customerPhone}
              onChange={(e) => setPaper({ customerPhone: e.target.value })}
            />
          </label>
          <label className="qf-wide">
            <span>Address</span>
            <textarea
              rows={2}
              value={doc.address}
              placeholder={'Street\nCity, state, ZIP'}
              onChange={(e) => setCustomer({ address: e.target.value })}
            />
          </label>
        </div>

        {lines.map((l) => (
          <Line key={l.id} item={l} />
        ))}

        {lines.length === 0 && <p className="hint">Add the work you are pricing.</p>}

        <div className="ql-add">
          {ORDER.map((c) => (
            <button key={c} onClick={() => addQuoteLine(c)}>
              + {CATEGORY_SETUP[c].label}
            </button>
          ))}
        </div>

        {/*
          THE LINES THAT ARE NOT DRAWN.
          
          Everything above is something you traced onto a photograph. This is
          the rest of the invoice - the work, the hardware and the visits a
          customer is genuinely charged for and that no drawing tool will ever
          produce a shape for.
          
          Takedown sits first deliberately. A season's profit lives in January,
          and a quote written without it has priced eleven months of a twelve
          month job. Putting it one press away is the difference between
          remembering it and remembering it in February.
        */}
        <p className="ql-add-label">Everything else</p>
        <div className="ql-add">
          {EXTRA_LINES.map((x) => (
            <button key={x.label} title={x.hint} onClick={() => addQuoteLine('other', x.label)}>
              + {x.label}
            </button>
          ))}
        </div>

        {/*
          THE LADDER, WHERE THE MONEY IS.

          It used to be a month in a dropdown at the top of the screen with the
          address and the document number, which is where you put a fact about
          a job, not where you put the price. The discount is the argument for
          signing today, so a contractor sitting in a driveway should be able to
          turn the phone around and let somebody read three numbers going up.

          Same arithmetic as the printed page: totalAt sums the rounded line
          prices, so the figure on the chip is the figure in the black box.
        */}
        <div className="rung-pick">
          <p className="ql-add-label">What the customer pays</p>
          {rungs.length > 0 ? (
            <>
              <div className="rungs">
                {rungs.map((t) => (
                  <button
                    key={t.month}
                    className="rung"
                    aria-pressed={paper.installMonth === t.month}
                    onClick={() => setPaper({ installMonth: t.month })}
                  >
                    <span className="rung-m">{MONTHS[t.month]}</span>
                    <span className="rung-off">
                      {t.offPct > 0 ? `${t.offPct}% off` : 'full price'}
                    </span>
                    <span className="rung-amt">{money(totalAt(lines, t.offPct))}</span>
                  </button>
                ))}
                <button
                  className="rung"
                  aria-pressed={paper.installMonth == null}
                  onClick={() => setPaper({ installMonth: null })}
                >
                  <span className="rung-m">No month</span>
                  <span className="rung-off">list price</span>
                  <span className="rung-amt">{money(subtotal(lines))}</span>
                </button>
              </div>
              <button className="link-btn" onClick={() => setLadder((v) => !v)}>
                {ladder ? 'Done with months' : 'Edit months and percentages'}
              </button>
            </>
          ) : (
            /* No ladder is a real answer. The month still goes on the paper. */
            <label className="rung-plain">
              <span>Install month</span>
              <select
                value={paper.installMonth ?? ''}
                onChange={(e) =>
                  setPaper({ installMonth: e.target.value === '' ? null : Number(e.target.value) })
                }
              >
                <option value="">No month on the quote</option>
                {MONTHS.map((m, i) => (
                  <option key={m} value={i}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        {/*
          IT SAYS BALANCE DUE ONCE MONEY HAS ACTUALLY ARRIVED, and not because
          somebody set a deposit percentage. Those were the same condition
          before, so an invoice could tell a homeowner they had paid half a job
          they had not paid a cent of.
        */}
        <div className="quote-total">
          <span>{paid > 0 ? 'Balance due' : 'Total'}</span>
          <strong>{money(paid > 0 ? balanceDue(lines, paper) : total(lines, paper))}</strong>
        </div>
        {discount(lines, paper) > 0 && (
          <p className="hint qf-sub">
            {money(subtotal(lines))} at list, less {money(discount(lines, paper))}
          </p>
        )}

        {/* --------------------------------- deposit, terms, payments ---- */}
        <details className="qsec" open={invoice}>
          <summary>Deposit and payment terms</summary>
          <div className="qf">
            <label>
              <span>This document is a</span>
              <select
                value={paper.kind}
                onChange={(e) => setPaper({ kind: e.target.value as 'quote' | 'invoice' })}
              >
                <option value="quote">Quote</option>
                <option value="invoice">Invoice</option>
              </select>
            </label>

            {/*
              NO DEPOSIT IS A REAL ANSWER AND IT IS THE DEFAULT. Plenty of
              shops take nothing up front, and a form that opens at fifty
              percent puts a demand on the quote that nobody made. Whatever is
              chosen here is remembered for the next job, because it is a
              policy of the business rather than a fact about this house.
            */}
            <label>
              <span>Deposit</span>
              <select
                value={paper.deposit.mode}
                onChange={(e) => setDeposit({ mode: e.target.value as DepositMode })}
              >
                <option value="none">No deposit</option>
                <option value="percent">Percent of the job</option>
                <option value="flat">Fixed amount</option>
              </select>
            </label>
            {paper.deposit.mode !== 'none' && (
              <label>
                <span>
                  {paper.deposit.mode === 'percent' ? 'How much percent' : 'How many dollars'}
                </span>
                <input
                  type="number"
                  min={0}
                  value={paper.deposit.value || ''}
                  onChange={(e) => setDeposit({ value: num(e.target.value) })}
                />
                <em className="qf-hint">{money(dep)} requested</em>
              </label>
            )}

            {/*
              WHEN THE REST IS DUE IS NOT THE DEPOSIT'S QUESTION. Collapsing
              them is how an invoice ends up telling a homeowner the whole
              balance was due the day they received it.
            */}
            <label>
              <span>The balance is due</span>
              <select
                value={paper.dueOn}
                onChange={(e) => setPaper({ dueOn: e.target.value as DueOn })}
              >
                <option value="completion">On completion</option>
                <option value="date">On a date</option>
              </select>
            </label>
            {paper.dueOn === 'date' && (
              <label>
                <span>Due date</span>
                <input
                  type="date"
                  value={paper.dueDate}
                  onChange={(e) => setPaper({ dueDate: e.target.value })}
                />
                {!paper.dueDate && <em className="qf-hint bad">Pick a day, or say on completion.</em>}
              </label>
            )}
          </div>

          <Payments lines={lines} />
        </details>

        {/* ------------------------------------------- seasonal ladder ---- */}
        <details
          className="qsec"
          open={ladder}
          onToggle={(e) => setLadder((e.currentTarget as HTMLDetailsElement).open)}
        >
          <summary>Months and percentages</summary>
          <div className="qf">
            <p className="hint qf-wide">
              A month and a percentage off. Most shops discount an early booking, and
              plenty of shops do not: leave this empty and every quote is list price.
            </p>

            {paper.tiers.map((t, i) => (
              <div className="qtier qf-wide" key={i}>
                <select
                  value={t.month}
                  onChange={(e) => setTier(i, { month: Number(e.target.value) })}
                >
                  {MONTHS.map((m, k) => (
                    <option key={m} value={k}>
                      {m}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min={0}
                  max={90}
                  value={t.offPct || ''}
                  placeholder="0"
                  onChange={(e) => setTier(i, { offPct: num(e.target.value) })}
                />
                <span className="qf-hint">% off, so {money(totalAt(lines, t.offPct))}</span>
                <button
                  className="ql-x"
                  aria-label={`Remove ${MONTHS[t.month]}`}
                  onClick={() =>
                    setPaper({ tiers: paper.tiers.filter((_, k) => k !== i) })
                  }
                >
                  &times;
                </button>
              </div>
            ))}

            <div className="ql-add qf-wide">
              <button
                onClick={() =>
                  setPaper({
                    tiers: [
                      ...paper.tiers,
                      { month: nextMonth(paper.tiers), offPct: 0 },
                    ],
                  })
                }
              >
                + Another month
              </button>
              {paper.tiers.length > 0 && (
                <button onClick={() => setPaper({ tiers: [], installMonth: null })}>
                  No seasonal pricing
                </button>
              )}
            </div>

            <label className="qf-wide qf-check">
              <input
                type="checkbox"
                checked={paper.showLadder}
                onChange={(e) => setPaper({ showLadder: e.target.checked })}
              />
              <span>Print the months on the quote, so waiting has a price</span>
            </label>

            <label className="qf-wide">
              <span>What the discount is called</span>
              <input
                type="text"
                value={paper.discountLabel}
                placeholder={discountLabel({ ...paper, discountLabel: '' })}
                onChange={(e) => setPaper({ discountLabel: e.target.value })}
              />
            </label>
          </div>
        </details>

        {/* ------------------------------------------- number and date --- */}
        {/*
          Assigned and today. Two fields nobody fills in and everybody wants to
          be able to see, so the summary is the answer and opening it is for the
          once a season when a number has to be forced.
        */}
        <details className="qsec">
          <summary>
            Number and date{' '}
            <em className="qf-hint">
              {[docNumber(paper, company.prefix), longDate(paper.date)]
                .filter(Boolean)
                .join('  \u00b7  ')}
            </em>
          </summary>
          <div className="qf">
            <label>
              <span>Number</span>
              <input
                type="text"
                value={paper.number}
                placeholder="Assigned automatically"
                onChange={(e) => setPaper({ number: e.target.value })}
              />
            </label>
            <label>
              <span>Date</span>
              <input
                type="date"
                value={paper.date}
                onChange={(e) => setPaper({ date: e.target.value })}
              />
            </label>
          </div>
        </details>

        {/* ------------------------------------------------ your company -- */}
        <details className="qsec">
          <summary>Your company</summary>
          <CompanyFields />
        </details>

        {/* ------------------------------------------------------- terms -- */}
        <details className="qsec">
          <summary>Terms</summary>
          <div className="qf">
            <label className="qf-wide">
              <span>Left column</span>
              <textarea
                rows={3}
                value={paper.termsLeft}
                onChange={(e) => setPaper({ termsLeft: e.target.value })}
              />
            </label>
            <label className="qf-wide">
              <span>Right column</span>
              <textarea
                rows={3}
                value={paper.termsRight}
                onChange={(e) => setPaper({ termsRight: e.target.value })}
              />
            </label>
          </div>
        </details>
      </div>

      {showDoc && <QuoteDoc onClose={() => setShowDoc(false)} />}
    </div>
  )
}
